import React, {useEffect, useState} from 'react';
import {useDebounce} from '../../hooks/useDebunce';
import {useQuery} from '@tanstack/react-query';
import QUERY_KEYS from '../../constants/queryKeys';
import {handleGetAllUsersService} from '../../services/users';
import {UsersListResponse} from '../../pages/users/components/UserScreens/users';
import {ApiResponse} from '../../services/axiosInstance';
import {useClientsContext, getLocationFilterParams} from '../clients/ClientsContextProvider';

interface UsersContextType {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  selectedUsers: Array<string | number>;
  setSelectedUsers: React.Dispatch<
    React.SetStateAction<Array<string | number>>
  >;
  selectedUser: any;
  setSelectedUser: React.Dispatch<React.SetStateAction<any>>;
  showAddEditDialog: boolean;
  setShowAddEditDialog: React.Dispatch<React.SetStateAction<boolean>>;

  allUsers: UsersListResponse | undefined;
  refetchAllUsers: () => void;
  menuSelectedItem: string;
  setMenuSelectedItem: React.Dispatch<React.SetStateAction<string>>;
}

const UsersContext = React.createContext<UsersContextType | null>(null);

type Props = {
  children: React.ReactNode;
};

function UsersContextProvider({children}: Props) {
  const {selectedLocation} = useClientsContext();

  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Array<string | number>>(
    [],
  );
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showAddEditDialog, setShowAddEditDialog] = useState(false);
  const [menuSelectedItem, setMenuSelectedItem] = useState("archived");

  const debouncedSearch = useDebounce(searchText, 500);

  const {data: allUsers, refetch: refetchAllUsers} = useQuery<
    ApiResponse<UsersListResponse>,
    Error,
    UsersListResponse
  >({
    queryKey: [
      QUERY_KEYS.GET_ALL_USERS,
      page,
      selectedLocation,
      debouncedSearch,
      menuSelectedItem,
    ],
    queryFn: () =>
      handleGetAllUsersService({
        page: page,
        limit: 50,
        isPaginated: true,
        search: debouncedSearch,
        ...(menuSelectedItem !== 'archived' && {status: 'active'}),
        ...getLocationFilterParams(selectedLocation),
      }),
    enabled: !!selectedLocation,
  });

  useEffect(() => {
    setPage(1);
    setSelectedUsers([]);
  }, [selectedLocation]);

  return (
    <UsersContext.Provider
      value={{
        page,
        setPage,
        searchText,
        setSearchText,
        selectedUsers,
        setSelectedUsers,
        selectedUser,
        setSelectedUser,
        showAddEditDialog,
        setShowAddEditDialog,

        allUsers,
        refetchAllUsers,

        menuSelectedItem,
        setMenuSelectedItem,
      }}>
      {children}
    </UsersContext.Provider>
  );
}

export default UsersContextProvider;

export const useUsersContext = () => {
  const context = React.useContext(UsersContext);
  if (context === null) {
    throw new Error(
      'useUsersContext must be used within an UsersContextProvider',
    );
  }
  return context;
};
